/**
 * Work-email blocklist (v1) — shared, client-safe primitives.
 *
 * The authoritative check lives in `is-work-email.server.ts`, which matches against
 * the exhaustive `free-email-domains` + `disposable-email-domains` npm lists (~2.6MB
 * combined). Those lists must NEVER reach the browser bundle, so this module ships
 * only a small curated free-domain set for the best-effort inline hint.
 *
 * Ceiling (be honest): even the server check can only block *known* free/disposable
 * domains — it cannot prove an address belongs to the claimed company, and legit
 * custom domains always pass. See scratchpad 63 "work-email validation" research.
 */
export const WORK_EMAIL_REJECT_REASONS = ['invalid', 'free', 'disposable'] as const;
export type WorkEmailRejectReason = (typeof WORK_EMAIL_REJECT_REASONS)[number];
export type WorkEmailResult = { ok: true } | { ok: false; reason: WorkEmailRejectReason };

/** Query param /api/form-submit sets alongside `?error=work-email-required` so the
 * rejection card can fire a per-reason Fathom event (Fathom events carry no properties).
 * Namespaced so it can't collide with a generic `reason` on marketing links. */
export const WORK_EMAIL_REASON_PARAM = 'error_reason';

// Mirrors Pardot's server-side format check (see validateEmail in pardot-data.ts).
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Lowercased domain of a well-formed address, or null if the address is malformed. */
export function emailDomain(email: string): string | null {
  const normalized = email.trim().toLowerCase();
  if (!EMAIL_FORMAT.test(normalized)) {
    return null;
  }
  return normalized.slice(normalized.lastIndexOf('@') + 1);
}

// The most common consumer/free providers — a curated subset of the full
// free-email-domains list, small enough to ship to the browser. Kept a strict
// subset so the client hint never disagrees with the server's verdict on these.
const COMMON_FREE_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.co.in',
  'yahoo.fr',
  'ymail.com',
  'outlook.com',
  'hotmail.com',
  'hotmail.co.uk',
  'hotmail.fr',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'gmx.com',
  'gmx.de',
  'gmx.net',
  'mail.com',
  'zoho.com',
  'yandex.com',
  'yandex.ru',
  'fastmail.com',
  'qq.com',
  '163.com',
  '126.com',
  'naver.com',
  'web.de',
  't-online.de',
  'orange.fr',
  'free.fr',
  'comcast.net',
  'verizon.net',
  'sbcglobal.net',
  'att.net',
  'rediffmail.com',
]);

/** Inline hint shown on strict forms (client-side). Server is the enforcer. */
export const WORK_EMAIL_WARNING =
  "Please use your work email. We can't accept personal addresses like Gmail or Outlook.";

/** Shown under the warning once a submit has been blocked, so the jump back to the email
 * field doesn't read as a glitch. Followed by the free Build Scan fallback link. */
export const WORK_EMAIL_NOT_SENT = "Your request wasn't sent.";

/**
 * Best-effort CLIENT hint: true when the address is a well-formed personal/free
 * email from a provider we recognise. Malformed input returns false — native
 * email/pattern validation already covers that. The server is the authoritative
 * enforcer and also blocks disposable domains + the long tail of free providers.
 */
export function looksLikePersonalEmail(email: string): boolean {
  const domain = emailDomain(email);
  return domain !== null && COMMON_FREE_DOMAINS.has(domain);
}
